import styles from './Avatar.module.css';

/**
 * Figma: `_sidebar menu item media` (80:1839) — `Size` × `Type`, since the
 * 2026-09-21 update:
 *
 *   Size=xl  48x48, radius-xl  — the profile photo on Settings → Account
 *   Size=lg  32x32, radius-sm
 *   Size=md  28x28, radius-xs  — the profile header in the sidebar footer menu
 *   Size=sm  20x20, radius-xs  — the sidebar footer trigger
 *
 * `Type=Image` is a logo; `Type=Placeholder` is initials in `text/placeholder`
 * — `Text md/Regular` at `lg`, `Text sm/leading-none/Regular` at `md` and `sm`.
 * Both types share the tile: `surface/background` with an INSIDE
 * `border/secondary` ring. (It was `surface/card` over `border/primary` until
 * this sync; every variant now binds the two above.)
 *
 * The update renamed the sizes as well as adding one: the 32 tile that was `md`
 * is `lg`, and `md` is new — 24 at first, then 28 when the profile header
 * grew. Only `sm` was in use, so nothing moved.
 *
 * Figma's image fill is FIT, so the image is `object-fit: contain` — the whole
 * mark stays visible rather than being cropped to fill the tile.
 *
 * **Except at `xl`** (2147:69319 / 2147:69321, added later on 2026-09-21),
 * whose image fill is FILL — `object-fit: cover`. That is the right call for
 * what `xl` holds: a person's photo, where letterboxing a portrait inside a
 * square would leave bars either side of a face. Its initials are
 * `Text lg/Medium`, the only size that sets them Medium.
 */
export type AvatarSize = 'xl' | 'lg' | 'md' | 'sm';

type AvatarBaseProps = {
  size?: AvatarSize;
  /**
   * How an image fills the tile. Defaults to the size's own Figma scale mode —
   * `cover` at `xl` (FILL), `contain` everywhere else (FIT, for logos).
   *
   * Not in Figma, which only ever puts a person's photo in the `xl` tile. The
   * sidebar's profile header is the case that needs it: it is `md`, whose FIT
   * mode is right for the workspace logo, and a portrait letterboxed inside a
   * 28px square reads as a broken image. A photo passes `cover` wherever it
   * sits. Ignored for initials.
   */
  fit?: 'contain' | 'cover';
  className?: string;
};

export type AvatarProps = AvatarBaseProps &
  (
    | {
        /** Figma `Type=Image`. */
        src: string;
        /**
         * Accessible name. Pass an empty string when an adjacent label already
         * names the entity — the sidebar footer and its menu both do.
         */
        alt: string;
        initials?: never;
      }
    | {
        /**
         * Figma `Type=Placeholder` — the letters drawn on the tile, e.g. `JD`.
         * Decorative: the name beside it is what a screen reader should read.
         */
        initials: string;
        src?: never;
        alt?: never;
      }
  );

const sizeClass = { xl: styles.xl, lg: styles.lg, md: styles.md, sm: styles.sm } as const;

export function Avatar({ size = 'lg', fit, className, ...props }: AvatarProps) {
  const classes = [
    styles.avatar,
    sizeClass[size],
    fit === 'cover' ? styles.cover : fit === 'contain' ? styles.contain : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (props.initials !== undefined) {
    return (
      <span className={[classes, styles.placeholder].join(' ')} aria-hidden="true">
        {props.initials}
      </span>
    );
  }

  return (
    <span className={[classes, styles.withImage].join(' ')}>
      <img className={styles.image} src={props.src} alt={props.alt} />
    </span>
  );
}
